import React from 'react'
import PublicLayout from '../components/PublicLayout'
import SignupCTA from '../components/SignupCTA'
import { CheckIcon } from '../components/Icons'
import { TIERS } from '../tiers'

const FAQ = [
  {
    q: 'Is there a free trial?',
    a: 'Yes. You can set up your profile and start receiving digests before you pick a paid plan.',
  },
  {
    // Said "changes take effect immediately" until 2026-10-16, which
    // contradicted Terms §3. Owner decision: a cancellation runs to the end of
    // the paid period, and the Stripe Customer Portal must be set to match.
    q: 'Can I change plans later?',
    a: 'Anytime. Upgrade, downgrade or cancel from your account. If you cancel, you keep access until the end of the month you have paid for.',
  },
  {
    // Was "Annual plans (two months free) are coming soon." Dropped rather than
    // aged further: it has said "coming soon" for months, Stripe is still on
    // test keys (issue #27), and a promise with no date reads as neglect.
    q: 'Do you offer annual billing?',
    a: 'Not yet. Every plan is billed monthly today, and you can cancel from your account at any time.',
  },
]

export default function Pricing() {
  return (
    <PublicLayout
      title="Pricing"
      description="Praenum plans start at $39 a month. Every plan includes foundation leads, federal grants and a weekly digest; Pro adds search across every grantmaker on record. No contracts, cancel anytime."
    >
      <section className="bg-gradient-to-b from-brand-100 to-white">
        <div className="max-w-3xl mx-auto px-4 pt-20 pb-10 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-brand-900">
            Pricing that pays for itself
          </h1>
          {/* Dropped "at the best value of any grant-monitoring tool": an
              unprovable comparative claim, and the owner does not want the site
              measuring itself against competitors (2026-08-13). */}
          <p className="mt-4 text-lg text-gray-600">
            Every plan includes foundation leads and federal grants. One grant you would
            have missed covers Praenum for years. Start free, upgrade when you are ready,
            cancel anytime.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16 -mt-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map(tier => (
            <div
              key={tier.key}
              className={`relative rounded-2xl border p-7 flex flex-col bg-white ${
                tier.highlight
                  ? 'border-brand-300 ring-2 ring-brand-100 shadow-sm'
                  : 'border-gray-200'
              }`}
            >
              {tier.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-500 text-white text-xs font-medium px-3 py-1">
                  Most popular
                </span>
              )}
              <h3 className="font-semibold text-gray-900 text-lg">{tier.name}</h3>
              <p className="mt-1 text-sm text-gray-500 min-h-[2.5rem]">{tier.tagline}</p>
              <p className="mt-4">
                <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                <span className="text-sm text-gray-500">/mo</span>
              </p>
              <div className="mt-5">
                <SignupCTA
                  variant={tier.highlight ? 'primary' : 'secondary'}
                  fullWidth
                  mailto={false}
                />
              </div>
              <ul className="mt-6 space-y-3 text-sm">
                {tier.features.map(f => (
                  <li key={f} className="flex items-start gap-2 text-gray-600">
                    <CheckIcon className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-gray-500">
          All plans include the relevance feedback loop, reliable inbox delivery, and
          cancel-anytime billing. Secure payments handled by Stripe.
        </p>
      </section>

      {/* Pricing FAQ */}
      <section className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-16">
          <h2 className="text-2xl font-bold text-brand-900 text-center">Billing questions</h2>
          <div className="mt-8 space-y-4">
            {FAQ.map(item => (
              <div key={item.q} className="rounded-xl border border-gray-200 bg-white p-6">
                <h3 className="font-semibold text-gray-900">{item.q}</h3>
                <p className="mt-2 text-sm text-gray-600">{item.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-col items-center">
            <SignupCTA label="Get started free" size="lg" />
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
